import { Linking, StyleSheet, TouchableOpacity, View } from 'react-native';
import { mvs } from 'react-native-size-matters';

import { useAppSelector } from '../../app/hooks';
import { COLORS, isTablet } from '../../constants';
import {
  selectDivRadius,
  selectMainColor,
} from '../../features/appconfig/store/selectors';
import { useAdjustedFontSize } from '../../hooks/useAdjustedFontSize';
import i18n from '../../localization/i18n';
import { PodcastModel } from '../../models/Podcast.model';
import { CustomIcon } from '../CustomIcon';
import CustomText from '../CustomText';

export interface PodcastMetaProps {
  podcast: PodcastModel | null;
}

export const PodcastMeta = ({ podcast }: PodcastMetaProps) => {
  const divRadius = useAppSelector(selectDivRadius);
  const mainColor = useAppSelector(selectMainColor);
  const metaTextFontSize = useAdjustedFontSize(mvs(14));
  return (
    <View style={[styles.metaContainer, { borderRadius: mvs(divRadius) }]}>
      <View style={isTablet ? styles.tabletLayout : styles.mobileLayout}>
        {podcast?.author && (
          <View style={styles.metaRow}>
            <CustomIcon name="user" size={mvs(16)} style={styles.icon} color="#252525" />
            <CustomText
              style={[styles.metaText, { fontSize: metaTextFontSize }]}
              numberOfLines={1}
            >
              {podcast?.author}
            </CustomText>
          </View>
        )}

        {podcast?.url && (
          <View style={styles.metaRow}>
            <CustomIcon name="link" size={mvs(16)} style={styles.icon} color="#252525" />
            <TouchableOpacity
              onPress={() => {
                Linking.openURL(podcast?.url);
              }}
              style={styles.metaTextContainer}
            >
              <CustomText
                style={[
                  styles.metaText,
                  { fontSize: metaTextFontSize, color: mainColor },
                ]}
                numberOfLines={1}
              >
                {podcast?.url}
              </CustomText>
            </TouchableOpacity>
          </View>
        )}

        {/* categories */}
        {podcast?.categories && podcast?.categories.length > 0 && (
          <View style={styles.metaRow}>
            <CustomIcon name="tag" size={mvs(16)} style={styles.icon} color="#252525" />
            <CustomText
              style={[styles.metaText, { fontSize: metaTextFontSize }]}
              numberOfLines={1}
            >
              {podcast?.categories.join(', ')}
            </CustomText>
          </View>
        )}

        {/* explicit content */}
        {podcast?.explicit && (
          <View style={styles.metaRow}>
            <CustomIcon
              name="explicit"
              size={mvs(16)}
              style={styles.icon}
              color="#252525"
            />
            <CustomText
              style={[styles.metaText, { fontSize: metaTextFontSize }]}
              numberOfLines={1}
            >
              {i18n.t('podcast.explicitContent')}
            </CustomText>
          </View>
        )}
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  metaContainer: {
    backgroundColor: COLORS.WHITE,
    padding: mvs(12),
    gap: mvs(10),
    width: '100%',
  },
  mobileLayout: {
    flexDirection: 'column',
    gap: mvs(10),
  },
  tabletLayout: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: mvs(16),
    justifyContent: 'space-between',
  },
  icon: {
    opacity: 0.4,
  },
  metaRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: mvs(8),
    minWidth: isTablet ? '45%' : undefined,
    maxWidth: '100%',
  },
  metaTextContainer: {
    flex: 1,
  },
  metaText: {
    flex: 1,
  },
});

export default PodcastMeta;
